<template>
  <card-component
    class="resource lesson-detail lesson"
    size="large"
    :subject-key="subjectKey"
    :class="cssClass"
    truncate
    @on-click="handlePrimaryCardAction"
  >
    <template #shadow>
      <div class="card-shadow" />
    </template>
    <template #header>
      <div v-if="hasGamification" class="game-watermark game-watermark-active"></div>

      <label-component
        v-if="isStudentGroupResource"
        class="group-name"
        kind="class-group"
        :text="studentGroupName"
        :subject-key="subjectKey"
      />
      <label-component
        v-else
        kind="domain"
        :class="{
          moe: isMOE,
          community: isCG,
        }"
        :text="getLocation(contextData.resourceDomain)"
      />
      <h6>{{ contextData.title }}</h6>
    </template>
    <template #metadata>
      <li>
        <span v-if="processedData.latestSubject">{{ processedData.latestSubject }}</span>
        <span v-if="processedData.latestLevelAndStream">
          {{ processedData.latestLevelAndStream.displayName }}
        </span>
      </li>
      <li>
        <span>{{ packageTypeLabel }}</span>
      </li>
    </template>

    <template #content>
      <div class="thumbnail-placeholder">
        <img v-if="hasThumbnail" :src="contextData.thumbnailUrl" class="thumbnail" />
      </div>
    </template>
  </card-component>
</template>

<script setup lang="ts">
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant.ts";
import { STUDENT_GROUP_RESOURCE_TYPE } from "@modules/assignment/service/assignment.constant";
import { isEmpty, notNull } from "@utils/var.check";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

// Props
interface Props {
  contextData?: Record<string, unknown>;
}

const { contextData = null } = defineProps<Props>();

const { t } = useI18n();
const router = useRouter();

// Data
const processedData = ref(contextData);

// Computed
const isMOE = computed(() => {
  return contextData.resourceDomain === "MOE";
});

const isCG = computed(() => {
  return contextData.resourceDomain === "COMMUNITY" || contextData.resourceDomain === "Community";
});

const subjectKey = computed(() => {
  return isEmpty(processedData.value.latestSubjectKey)
    ? contextData.subjectKey
    : processedData.value.latestSubjectKey;
});

const studentGroupName = computed(() => {
  return contextData.groupName;
});

const hasGamification = computed(() => {
  return contextData.gameEnabled;
});

const hasThumbnail = computed(() => {
  return contextData && notNull(contextData.thumbnailUrl);
});

const packageTypeLabel = computed(() => {
  return t("course_label");
});

const isStudentGroupResource = computed(() => {
  return contextData.studentGroupResourceType === STUDENT_GROUP_RESOURCE_TYPE.CLASS_GROUP_RESOURCE;
});

const cssClass = computed(() => {
  return [
    {
      moe: isMOE.value,
      community: isCG.value,
      "_has-thumbnail": hasThumbnail.value,
      "_has-gamification": hasGamification.value,
    },
  ];
});

// Methods
const getLocation = (domain: string) => {
  switch (domain) {
    case "PERSONAL":
      return t("my_drive_label");
    case "MOE":
      return t("moe_library_label");
    case "COMMUNITY":
    case "Community":
      return t("community_gallery_label");
    case "ALP_TEMPLATE":
      return t("alp_template_label");
    default:
      return "";
  }
};

const handlePrimaryCardAction = () => {
  handleViewLessonEditor();
};

const handleViewLessonEditor = () => {
  // TODO: SLS-11043 (OK), to refactor, use router.resolve
  // TODO: SLS-11043 (OK), editor
  let lessonUuid = "";
  switch (contextData.resourceDomain) {
    case "PERSONAL":
      lessonUuid = contextData.uuid;
      break;
    case "MOE":
      lessonUuid = contextData.uuid;
      break;
    case "COMMUNITY":
      lessonUuid = contextData.communityResource.uuid;
      break;
  }
  const routeData = router.resolve({
    name: "EditorCover",
    params: {
      resourceDomain: RESOURCE_DOMAIN_PATH[contextData.resourceDomain],
      mode: "view",
      lessonUuid,
    },
    query: {
      ...(isMOE.value && { version: lessonUuid }),
    },
  });
  window.open(routeData.href);
};
</script>
